import { useCallback, useEffect, useMemo, useState } from 'react'
import { uniqBy } from 'lodash'
import { ShopifyProductWithBundles } from '@/services/Shopify/ShopifyService'
import { useTranslate } from '@/utils/translate/translate-client'
import { QuantityMultipliers } from '@/views/PreSales/PreSalesGiveawayView/config/QuantityMultipliers'
import { useGiveawaySchedule } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawaySchedule'

interface Props {
  tickets: ShopifyProductWithBundles[]
  productTitleToFilter: string
  defaultTicketPackId: string
  theatricalSlug: string
}

export const useGiveawayProductController = ({
  tickets,
  productTitleToFilter,
  defaultTicketPackId,
  theatricalSlug,
}: Props) => {
  const [selectedProduct, setSelectedProduct] = useState<ShopifyProductWithBundles | null>(null)

  const { t } = useTranslate('tickets')
  const { normal, dailyBonusEntries, minRewardTicketQuantity } = useGiveawaySchedule(theatricalSlug)
  const multiplier = normal?.multiplier ?? 1

  useEffect(() => {
    const ticketPack = tickets?.find((offer) => {
      return offer?.id === defaultTicketPackId
    })
    if (ticketPack) setSelectedProduct(ticketPack)
  }, [tickets, defaultTicketPackId])

  const selectedTicketsQty = useMemo(
    () =>
      selectedProduct?.bundledProducts?.find((product) => productTitleToFilter === product?.product?.title)?.quantity ??
      0,
    [productTitleToFilter, selectedProduct?.bundledProducts],
  )

  const handleProductChange = useCallback(
    (offerId: string) => {
      const offerToSelect = tickets.find((product) => product?.id === offerId) as ShopifyProductWithBundles
      if (offerToSelect) setSelectedProduct(offerToSelect)
    },
    [tickets],
  )

  const getEntriesByQuantity = useCallback(
    (quantity: number) => {
      const quantityMultiplier = getQuantityMultiplier(theatricalSlug, quantity)
      const bonusDailyEntries = quantity >= minRewardTicketQuantity ? dailyBonusEntries : 0

      if (quantityMultiplier > 1) return quantity * quantityMultiplier + bonusDailyEntries

      return quantity * multiplier + bonusDailyEntries
    },
    [dailyBonusEntries, minRewardTicketQuantity, multiplier, theatricalSlug],
  )

  const allPerks = useMemo(() => {
    const perks = tickets
      ?.flatMap((product) => product?.bundledProducts)
      ?.map((bundle) => bundle?.product)
      ?.filter((product) => product?.title !== productTitleToFilter)

    const dynamicPerks = [
      {
        id: 'dynamic-tickets',
        title: t('homesteadMovieTicketsCount', '{{count}} Homestead Movie Tickets', { count: selectedTicketsQty }),
      },
      {
        id: 'dynamic-entries',
        title: t('giveawayEntriesCount', '{{count}} Giveaway Entries', {
          count: getEntriesByQuantity(selectedTicketsQty),
        }),
      },
    ]

    return [...dynamicPerks, ...uniqBy(perks, 'id')]
  }, [tickets, t, selectedTicketsQty, getEntriesByQuantity, productTitleToFilter])

  const activePerkIds = useMemo(() => {
    return new Set([
      ...(allPerks ?? [])
        .filter((perk) => selectedProduct?.bundledProducts?.some((product) => product?.product?.id === perk?.id))
        .map((perk) => perk?.id),
    ])
  }, [allPerks, selectedProduct?.bundledProducts])

  const checkIsPerkActive = useCallback(
    (perkId: string) => {
      if (perkId === 'dynamic-tickets' || perkId === 'dynamic-entries') {
        return true
      }
      return activePerkIds.has(perkId)
    },
    [activePerkIds],
  )

  const handlePerkClick = useCallback(
    (perkId: string) => {
      const shouldSkip = !!selectedProduct?.bundledProducts?.some((offer) => offer?.product?.id === perkId)
      if (shouldSkip) return

      const productToSelect = tickets?.find((offer) =>
        offer?.bundledProducts?.some((product) => product?.product?.id === perkId),
      )
      if (productToSelect) setSelectedProduct(productToSelect)
    },
    [selectedProduct?.bundledProducts, tickets],
  )

  const ticketQuantityList = useMemo(() => {
    return tickets
      ?.map((offer) => {
        const quantity =
          offer?.bundledProducts?.find((bundle) => bundle?.product?.title === productTitleToFilter)?.quantity ?? 0
        return {
          id: offer.id,
          quantity,
          entries: getEntriesByQuantity(quantity),
        }
      })
      ?.sort((a, b) => a.quantity - b.quantity)
  }, [tickets, getEntriesByQuantity, productTitleToFilter])

  return useMemo(() => {
    return {
      selectedProduct,
      activePerkIds,
      handleProductChange,
      selectedTicketsQty,
      ticketQuantityList,
      checkIsPerkActive,
      allPerks,
      totalSelectedEntries: getEntriesByQuantity(selectedTicketsQty),
      selectedQuantityMultiplier: getQuantityMultiplier(theatricalSlug, selectedTicketsQty),
      handlePerkClick,
    }
  }, [
    selectedProduct,
    activePerkIds,
    handleProductChange,
    selectedTicketsQty,
    ticketQuantityList,
    checkIsPerkActive,
    allPerks,
    getEntriesByQuantity,
    theatricalSlug,
    handlePerkClick,
  ])
}

export const getQuantityMultiplier = (theatricalSlug: string, quantity: number) => {
  const quantityMultipliers = QuantityMultipliers[theatricalSlug]

  return (
    quantityMultipliers?.find((multiplier) => quantity >= multiplier.min && quantity <= multiplier.max)?.multiplier ?? 1
  )
}
